import faker from "faker";
import { AccountType } from "component-library";
import {
  ISelfTapeRequestStatus,
  MediaRequestType,
  MemberGroups,
} from "shared-types";
import { LinkmailType } from "shared/models/breakdown";
import { FacetCategory } from "shared/models/facet";
import { IUser } from "shared/models/user";

import { DEFAULT_CLIENT_AGENCY_ID } from "config/common";

import { IPhoto } from "models/api";
import {
  AlertType,
  ClientAlertID,
  IClientAlert,
  IClientAlertData,
} from "models/clientAlert";
import { IJobItem, IReportConcernData, JobItemType } from "models/job";
import {
  ISelfTapeRequestNotification,
  ISelfTapeRequestNotificationFilters,
} from "models/selfTapeRequest";
import {
  IAgent,
  IMakeSuggestionsPerformer,
  IMakeSuggestionsPerformersStatsResponse,
  INudge,
  ISuggestion,
  MembershipTypes,
} from "models/suggestion";
import { ICommonTranslation } from "models/translation";

import { IAgencyPermission } from "shared/services/api/agents/agentsService";

import {
  FilterControlType,
  FilterType,
  IFilterConfig,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { closingDateKeys } from "pages/JobsPage/shared/shared.helpers";
import { ISubmissionUploadMedia } from "pages/SelfTapeUploadPage/SelfTapeUploadPage";

import { IBreakdownViewRole } from "components/BreakdownView/BreakdownView";
import { readUnreadButtonOptionsKeys } from "components/JobsTable/JobsTable.helpers";
import { IJobTableItem } from "components/JobsTable/JobsTable.models";
import { ISelfTapeRequestNotificationTableItem } from "components/SelfTapeRequestNotificationTable/SelfTapeRequestNotificationTable.models";
import { MOCK_PERFORMERS } from "./mockResponses/mockPerformerSearch";

export const DEFAULT_USER_TYPE = AccountType.Agent;
export const DEFAULT_USER_MEMBER_GROUP = MemberGroups.agent;
export const HOUR = 1000 * 60 * 60;
export const ONE_DAY = HOUR * 24;

export function getRandomString(): string {
  return faker.helpers.slugify(faker.lorem.word());
}

export function getRandomSentence(): string {
  return faker.lorem.sentence();
}

export function getRandomParagraph(): string {
  return faker.lorem.paragraph();
}

export function getRandomUuid(): string {
  return faker.random.uuid();
}

export function getRandomNumber(max?: number): number {
  return faker.random.number(max);
}

export function getDateISOString(): string {
  return faker.date.past().toISOString();
}

export function getRandomUrl(): string {
  return `${faker.internet.url()}/`;
}

export function getRandomBoolean(): boolean {
  return faker.random.boolean();
}

export function getRandomImageLink(): string {
  return getRandomUuid();
}

export function getRandomIntermediateDate(
  daysBeforeNow: number = 3,
  daysAfterNow: number = daysBeforeNow
) {
  const now = new Date();
  const startDay = new Date(now.getTime() - ONE_DAY * daysBeforeNow);
  const endDay = new Date(now.getTime() + ONE_DAY * daysAfterNow);
  return faker.date.between(startDay, endDay);
}

export function getUser(type: AccountType = DEFAULT_USER_TYPE): IUser {
  return {
    id: "1",
    type,
    email: getRandomString(),
    forenames: getRandomString(),
    surname: getRandomString(),
    restrictAccess: false,
    performerAccessType: 2,
    agencyList: [],
  };
}

export function getCommonTranslation(): ICommonTranslation {
  return {
    breakdowns: {
      linkmailTypeAmendment: getRandomString(),
      linkmailTypeDuplicate: getRandomString(),
      sentOnFormat: getRandomString(),
      closingDateLabels: [
        {
          TypeId: closingDateKeys.today,
          Translation: getRandomString(),
        },
        {
          TypeId: closingDateKeys.tomorrow,
          Translation: getRandomString(),
        },
        {
          TypeId: closingDateKeys.other,
          Translation: getRandomString(),
        },
        {
          TypeId: closingDateKeys.closed,
          Translation: getRandomString(),
        },
      ],
      viewExclusiveLabel: getRandomString(),
      viewExclusiveTooltip: getRandomString(),
      dateTimeFormat: getRandomString(),
      localHire: getRandomString(),
      isRoleCastLabelText: "Role Cast",
    },
    followups: {
      updateLabel: getRandomString(),
    },
    grid: {
      senderNameAnonymous: getRandomString(),
      readUnreadButtonOptions: [
        {
          TypeId: readUnreadButtonOptionsKeys.markAsRead,
          Translation: getRandomString(),
        },
        {
          TypeId: readUnreadButtonOptionsKeys.markAsUnread,
          Translation: getRandomString(),
        },
      ],
      resultStats: getRandomString(),
      selectAll: getRandomString(),
      refreshResultsLabel: getRandomString(),
    },
    pages: {
      pageTitleFormat: getRandomString(),
      pageTitles: [],
    },
    validationMessages: { validationMessagesRequired: getRandomString() },
    reportConcernPopup: {
      reportConcernCancelButton: getRandomString(),
      reportConcernDetailsPlaceholder: getRandomString(),
      reportConcernTypeDropdownLabel: getRandomString(),
      reportConcernSendButtonLoading: getRandomString(),
      reportConcernTypeDropdownPlaceholder: getRandomString(),
      reportConcernTitle: getRandomString(),
      reportConcernDetailsLabel: getRandomString(),
      reportConcernSendButton: getRandomString(),
    },
    print: {
      printCancelButton: getRandomString(),
      printSubmitButton: getRandomString(),
    },
  };
}

export function getBreakdownViewRole(): IBreakdownViewRole {
  return {
    id: getRandomUuid(),
    title: getRandomString(),
    description: getRandomString(),
    playingAge: getRandomString(),
    height: getRandomString(),
    appearance: getRandomString(),
    gender: getRandomString(),
    disability: getRandomString(),
    contractType: getRandomString(),
    contractTypeInfo: getRandomString(),
    linkAttachments: [],
    genderIds: [],
    appearanceIds: [],
    anyEthnicity: true,
    ethnicityLabel: getRandomString(),
  };
}

export function getBreakdownViewRoles(count: number = 3): IBreakdownViewRole[] {
  return Array(count)
    .fill(1)
    .map(() => getBreakdownViewRole());
}

export function getClientAlertData(): IClientAlertData {
  return {
    id: getRandomUuid(),
    performer: {
      forename: getRandomString(),
      surname: getRandomString(),
    },
    breakdown: {
      id: getRandomString(),
      name: getRandomString(),
      closingDate: getRandomIntermediateDate(1, 3),
    },
    role: {
      name: getRandomString(),
    },
    read: faker.random.boolean(),
    dateCreated: faker.date.past(),
    alertType: faker.random.arrayElement([
      AlertType.Suggestion,
      AlertType.Nudge,
    ]),
  };
}

export function getClientAlertID(type: AlertType, id: string): ClientAlertID {
  return `${type}__${id}`;
}

export function getClientAlert(): IClientAlert {
  const data = getClientAlertData();

  return {
    ...data,
    id: getClientAlertID(data.alertType, data.id),
  };
}

export function getJobItem(): IJobItem {
  return {
    title: getRandomString(),
    roleTitle: getRandomString(),
    sender: getRandomString(),
    isRead: faker.random.boolean(),
    isSuggested: faker.random.boolean(),
    isNudged: faker.random.boolean(),
    type: faker.random.arrayElement([
      JobItemType.Linkmail,
      JobItemType.Role,
      JobItemType.FollowUp,
    ]),
    closingDate: getRandomIntermediateDate(1, 3).toUTCString(),
    sentDate: faker.date.past().toUTCString(),
    amendmentCount: getRandomNumber(),
    contractTypeId: getRandomNumber(),
    productionTypeId: getRandomNumber(),
    linkmailType: faker.random.arrayElement([
      LinkmailType.Amendment,
      LinkmailType.Duplicate,
      LinkmailType.Default,
    ]),
    linkmailId: getRandomString(),
    followUpId: getRandomString(),
    roleId: getRandomString(),
    senderId: getRandomString(),
    freezeDate: faker.date.past().toUTCString(),
    isCast: faker.random.boolean(),
  };
}

export function getJobItems(count: number = 10): IJobItem[] {
  return Array(count)
    .fill(1)
    .map(() => getJobItem());
}

export function getSelfTapeRequestItem(): ISelfTapeRequestNotification {
  return {
    id: getRandomUuid(),
    notified: faker.date.past(15),
    acknowledged: faker.date.past(15),
    project: {
      id: getRandomUuid(),
      name: getRandomString(),
    },
    breakdown: {
      id: getRandomUuid(),
      name: getRandomString(),
    },
    roles: [
      {
        id: getRandomUuid(),
        name: getRandomString(),
      },
    ],
    performer: [
      {
        artistRef: getRandomString(),
        id: getRandomUuid(),
        name: getRandomString(),
      },
    ],
    castingDirector: {
      id: getRandomUuid(),
      name: getRandomString(),
    },
    notificationData: {
      requestId: getRandomUuid(),
      performerId: getRandomUuid(),
      title: getRandomString(),
      expiryDate: faker.date.past(15),
      description: getRandomString(),
      videosRequired: getRandomNumber(),
      status: ISelfTapeRequestStatus.Awaiting,
      mediaRequestType: MediaRequestType.SelfTape,
    },
  };
}

export function getSelfTapeRequestFilters(): ISelfTapeRequestNotificationFilters {
  return {
    performers: [{ id: getRandomString(), name: getRandomString() }],
    breakdownRoles: [
      {
        breakdown: [{ id: getRandomString(), name: getRandomString() }],
        roles: [{ id: getRandomString(), name: getRandomString() }],
      },
    ],
  };
}

export function getJobTableItem(): IJobTableItem {
  return {
    type: faker.random.arrayElement([
      JobItemType.Linkmail,
      JobItemType.Role,
      JobItemType.FollowUp,
    ]),
    id: getRandomString(),
    title: getRandomString(),
    sender: {
      id: getRandomString(),
      name: getRandomString(),
    },
    isRead: faker.random.boolean(),
    additionalInfo: [getRandomString(), getRandomString()],
    labels: [
      {
        text: getRandomString(),
        className: getRandomString(),
      },
    ],
    tags: Array(1)
      .fill(1)
      .map(getRandomString)
      .map((x) => ({
        filterType: FilterType.ContractType,
        facetCategory: FacetCategory.ContractType,
        facetId: getRandomNumber(),
        name: `#${x}`,
      })),
    url: getRandomString(),
  };
}

export function getSelfTapeRequestNotificationTableItem(): ISelfTapeRequestNotificationTableItem {
  return {
    id: getRandomUuid(),
    tags: Array(2)
      .fill(1)
      .map(() => ({ name: getRandomString(), id: getRandomUuid() })),
    sender: { name: getRandomString(), id: getRandomUuid() },
    title: getRandomString(),
    additionalInfo: [getRandomString(), getRandomString()],
    isRead: faker.random.boolean(),
    url: getRandomUrl(),
    sendStats: () => {},
  };
}

export function getJobTableItems(count: number = 10): IJobTableItem[] {
  return Array(count)
    .fill(1)
    .map(() => getJobTableItem());
}

export function getSelfTapeRequestNotificationTableItems(
  count: number = 10
): ISelfTapeRequestNotificationTableItem[] {
  return Array(count)
    .fill(1)
    .map(() => getSelfTapeRequestNotificationTableItem());
}

export function getClientAlertsData(count: number = 10): IClientAlertData[] {
  return Array(count)
    .fill(1)
    .map(() => getClientAlertData());
}

export function getClientAlerts(count: number = 10): IClientAlert[] {
  return Array(count)
    .fill(1)
    .map(() => getClientAlert());
}

export function getFilterConfig(): IFilterConfig {
  return {
    [FilterType.ProductionType]: {
      translations: getRandomString(),
      defaultValue: {},
      filterControlType: FilterControlType.Dropdown,
      drawerFilterControlType: FilterControlType.CheckboxGroup,
      facets: [],
      dropdownOptions: [{ label: getRandomString(), options: [] }],
      checkboxOptions: [],
    },
    [FilterType.ContractType]: {
      translations: getRandomString(),
      defaultValue: {},
      filterControlType: FilterControlType.Dropdown,
      drawerFilterControlType: FilterControlType.CheckboxGroup,
      facets: [],
      dropdownOptions: [{ label: getRandomString(), options: [] }],
      checkboxOptions: [],
    },
    [FilterType.DateSent]: {
      translations: getRandomString(),
      defaultValue: undefined,
      filterControlType: FilterControlType.DatePicker,
      drawerFilterControlType: FilterControlType.DatePicker,
    },
    [FilterType.Sender]: {
      translations: getRandomString(),
      defaultValue: undefined,
    },
    [FilterType.PlayingAgeMin]: {
      translations: getRandomString(),
      defaultValue: undefined,
    },
    [FilterType.PlayingAgeMax]: {
      translations: getRandomString(),
      defaultValue: undefined,
    },
    [FilterType.Locations]: {
      translations: {},
      defaultValue: [],
    },
  };
}

export function getReportConcernData(): IReportConcernData {
  return {
    type: getRandomString(),
    details: getRandomString(),
    linkmailId: getRandomUuid(),
    roleId: getRandomUuid(),
  };
}

export function getAgency(agencyId?: string): IAgent {
  return {
    id: agencyId || DEFAULT_CLIENT_AGENCY_ID.toString(),
    name: getRandomString(),
  };
}

export function getAgencyPermission(
  id?: number,
  allowNudge?: boolean,
  nudgeAllowed?: boolean,
  allowSelfSuggest?: boolean,
  suggestAllowed?: boolean
): IAgencyPermission {
  return {
    id: id ?? faker.random.number(),
    agencyId: id ?? faker.random.number(),
    name: getRandomString(),
    allowNudge: allowNudge !== undefined ? allowNudge : getRandomBoolean(),
    nudgeAllowed:
      nudgeAllowed !== undefined ? nudgeAllowed : getRandomBoolean(),
    allowSelfSuggest:
      allowSelfSuggest !== undefined ? allowSelfSuggest : getRandomBoolean(),
    suggestAllowed:
      suggestAllowed !== undefined ? suggestAllowed : getRandomBoolean(),
    performersReceiveBreakdownEmails: getRandomBoolean(),
  };
}

export function getNudge(): INudge {
  return {
    dateCreated: getRandomIntermediateDate(10, 10).toISOString(),
    agency: getRandomString(),
    id: getRandomUuid(),
  };
}

export function getMakeSuggestionsPerformer(
  agencyId?: string,
  isSelected: boolean = false,
  performerType?: MembershipTypes
): IMakeSuggestionsPerformer {
  const photos = getPhotos(5);

  return {
    performerType: performerType || MembershipTypes.performer,
    artistRef: getRandomString(),
    surname: getRandomString(),
    forenames: getRandomString(),
    agency: getAgency(agencyId),
    expiryDate: faker.date.future(1).toISOString(),
    dateOfBirth: faker.date.past(15).toISOString(),
    mainPhoto: photos[0],
    hasAudioClip: getRandomBoolean(),
    hasVideoClip: getRandomBoolean(),
    hasNudge: getRandomBoolean(),
    hasSelfSuggestion: getRandomBoolean(),
    gallery: photos.slice(1),
    suggestion: isSelected
      ? {
          id: getRandomString(),
          photo: photos[0].id,
          comment: getRandomString(),
        }
      : undefined,
    _links: {
      profile: {
        href: getRandomString(),
      },
    },
    tagminAvailability: [],
  };
}

export function getAgencies(count: number = 4): IAgent[] {
  return Array(count)
    .fill(1)
    .map(() => getAgency());
}

export function getMakeSuggestionsPerformers(
  count: number = 4,
  agencyId?: string
): IMakeSuggestionsPerformer[] {
  return Array(count)
    .fill(1)
    .map(() => getMakeSuggestionsPerformer(agencyId));
}

export function getMakeSuggestionsPerformersStats(): IMakeSuggestionsPerformersStatsResponse {
  return {
    appearances: [],
    heritageAndEthnicity: [],
  };
}

export function getPhoto(): IPhoto {
  return {
    id: getRandomUuid(),
    fileName: getRandomImageLink(),
  };
}

export function getPhotos(count: number = 4): IPhoto[] {
  return Array(count)
    .fill(1)
    .map(() => getPhoto());
}

export function getSuggestion(
  agencyId?: number,
  amendmentCount?: number
): ISuggestion {
  return {
    id: getRandomUuid(),
    photo: {
      fileName: getRandomImageLink(),
    },
    performer: {
      artistRef: getRandomString(),
      forenames: getRandomString(),
      surname: getRandomString(),
    },
    dateCreated: faker.date.past(),
    agency: {
      id: agencyId || getRandomNumber(10),
    },
    amendmentCount: amendmentCount || getRandomNumber(10),
    _links: {
      profile: {
        href: getRandomString(),
      },
    },
    comment: getRandomParagraph(),
    suggestedBy: getRandomString(),
  };
}

export function getSuggestions(
  count: number = 4,
  agencyId?: number
): ISuggestion[] {
  return Array(count)
    .fill(1)
    .map(() => getSuggestion(agencyId));
}

export function getSubmissionUploadMedia(): ISubmissionUploadMedia {
  return {
    id: getRandomUuid(),
    mediaInfoId: getRandomUuid(),
    title: getRandomString(),
  };
}

export function getSubmissionUploadMediaArray(
  count: number = 4
): ISubmissionUploadMedia[] {
  return Array(count)
    .fill(1)
    .map(() => getSubmissionUploadMedia());
}

export const getPerformers = (multiplier = 1) => {
  return [...Array(multiplier)].flatMap(() => MOCK_PERFORMERS);
};
